import type { Dispatch } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { ROOM_HEIGHT, ROOM_WIDTH } from "../editor/geometry";
import type { EditorAction } from "../editor/state";
import { pairKey } from "../editor/types";
import type { DraftPlan, DraftZone, Selection, Tool } from "../editor/types";
import { tableAccessibleName } from "./labels";
import { TableShape, type KeyCommand } from "./TableShape";
import { useTableDrag } from "./useTableDrag";

/**
 * The per-zone canvas (Implementation plan §3): a `<fieldset>` surface (native grouping + legend for
 * the accessible name) holding native-button tables, over an aria-hidden SVG that paints the grid and
 * the combinable-pair link lines (an overlay only — never the authority, FL-2). Move is pointer-drag
 * OR keyboard nudge; both dispatch element-granular reducer actions. A standing zone shows a headcount
 * panel instead of a table surface (it has no tables to place).
 */
export interface PlanCanvasProps {
  plan: DraftPlan;
  zone: DraftZone;
  selection: Selection;
  tool: Tool;
  linkAnchor: string | null;
  disabled: boolean;
  dispatch: Dispatch<EditorAction>;
}

export function PlanCanvas({
  plan,
  zone,
  selection,
  tool,
  linkAnchor,
  disabled,
  dispatch,
}: PlanCanvasProps) {
  const { t } = useTranslation();
  const linkMode = tool === "LINK";

  const drag = useTableDrag({
    disabled: disabled || linkMode,
    onCommit: (id, x, y) => dispatch({ type: "MOVE_TABLE", id, x, y }),
    onPick: (id) => dispatch({ type: "SELECT_TABLE", id }),
  });

  if (zone.kind === "STANDING_HEADCOUNT") {
    return (
      <fieldset
        className={css({
          p: "6",
          borderWidth: "1px",
          rounded: "lg",
          bg: "bg.subtle",
          color: "fg.muted",
        })}
      >
        <legend className={css({ px: "1", fontSize: "sm", fontWeight: "medium" })}>
          {t("floorPlan.canvas.standingZone", { name: zone.name })}
        </legend>
        <p className={css({ fontSize: "sm" })}>
          {t("floorPlan.canvas.standingHint", { capacity: zone.capacity ?? 0 })}
        </p>
      </fieldset>
    );
  }

  const tables = plan.tables.filter((tb) => tb.zoneId === zone.id);
  const byId = new Map(plan.tables.map((tb) => [tb.id, tb]));
  const inZone = new Set(tables.map((tb) => tb.id));
  const links = plan.combinables.filter((p) => inZone.has(p.a) && inZone.has(p.b));
  const pairedIds = new Set(plan.combinables.flatMap((p) => [p.a, p.b]));

  function handleActivate(id: string) {
    if (disabled) return;
    if (linkMode) dispatch({ type: "LINK_SELECT", id });
    else dispatch({ type: "SELECT_TABLE", id });
  }

  function handleKeyCommand(id: string, cmd: KeyCommand) {
    switch (cmd.type) {
      case "nudge":
        dispatch({ type: "NUDGE_TABLE", id, dx: cmd.dx, dy: cmd.dy });
        return;
      case "rotate":
        dispatch({ type: "ROTATE_TABLE", id, delta: cmd.delta });
        return;
      case "retire":
        dispatch({ type: "RETIRE_TABLE", id });
    }
  }

  return (
    // The P2 fix keeps the semantic `<fieldset>` + `<legend>` (accessible group name) but the grid
    // SVG below is now an IN-FLOW child (`display:block; width:100%; height:auto`, sized by its own
    // viewBox aspect ratio) instead of `position:absolute`. Previously EVERY child was out of flow
    // (absolute SVG + absolute tables), so the fieldset had no in-flow content and browsers collapsed
    // its block-size to 0 — the canvas showed no tables. The in-flow SVG now establishes the box
    // height; the absolutely-positioned tables overlay it (fieldset is `position:relative`).
    <fieldset
      data-plan-surface
      className={css({
        position: "relative",
        width: "100%",
        borderWidth: "1px",
        rounded: "lg",
        bg: "bg.default",
        overflow: "hidden",
        touchAction: "none",
        p: "0",
        m: "0",
      })}
    >
      <legend className={css({ srOnly: true })}>
        {t("floorPlan.canvas.zoneSurface", { name: zone.name, count: tables.length })}
      </legend>

      <svg
        aria-hidden
        viewBox={`0 0 ${ROOM_WIDTH} ${ROOM_HEIGHT}`}
        preserveAspectRatio="none"
        className={css({ display: "block", width: "100%", height: "auto" })}
      >
        <defs>
          <pattern id={`fp-grid-${zone.id}`} width={40} height={40} patternUnits="userSpaceOnUse">
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="var(--colors-border-muted)"
              strokeWidth={0.5}
            />
          </pattern>
        </defs>
        <rect width={ROOM_WIDTH} height={ROOM_HEIGHT} fill={`url(#fp-grid-${zone.id})`} />
        {links.map((p) => {
          const a = byId.get(p.a);
          const b = byId.get(p.b);
          if (!a || !b) return null;
          return (
            <line
              key={pairKey(p)}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="var(--colors-color-palette-default)"
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          );
        })}
      </svg>

      {tables.map((tb) => (
        <TableShape
          key={tb.id}
          table={tb}
          accessibleName={tableAccessibleName(tb, zone.name, t)}
          selected={selection.kind === "table" && selection.id === tb.id}
          linkMode={linkMode}
          isAnchor={linkAnchor === tb.id}
          paired={pairedIds.has(tb.id)}
          disabled={disabled}
          ghostX={drag.ghost?.id === tb.id ? drag.ghost.x : undefined}
          ghostY={drag.ghost?.id === tb.id ? drag.ghost.y : undefined}
          dragHandlers={drag.dragHandlers(tb)}
          onActivate={() => handleActivate(tb.id)}
          onKeyCommand={(cmd) => handleKeyCommand(tb.id, cmd)}
        />
      ))}

      {tables.length === 0 ? (
        <p
          className={css({
            position: "absolute",
            inset: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "fg.muted",
            fontSize: "sm",
            pointerEvents: "none",
          })}
        >
          {t("floorPlan.canvas.empty")}
        </p>
      ) : null}
    </fieldset>
  );
}
